import { FormattedMessage } from 'react-intl';
import { Fragment } from 'react';
import { ProductType } from '@tink/core/src/types';
import { MarketCode } from '@tink/config/src/markets';
import { ProductDimension } from '../../../models/product';
import { Provider, ProviderEntity } from '../../../models/provider';
import {
  buildAuthenticationTypePickerUrl,
  buildCredentialsTypeUrl,
  buildDisplayDescriptionUrl,
  buildFinancialInstitutionPickerUrl,
  buildFinancialServicePicker,
  buildMultipleAccessTypePickerUrl,
  buildSingleProviderUrl,
} from '../../../routes';
import { ProviderViewSetGroup } from '../../../utilities/provider-view-set';
import { GradientList } from '../../../components/molecules/GradientList/GradientList';
import { Caption } from '../../../components/atoms/Text/Text';
import { useConnectedProviders } from '../../../banking/components/multibanking/multibankingUtils';
import { applicationParameters } from '../../../utilities/application-parameters';
import {
  CantFindYourBankListItem,
  EmptyResult,
  List,
  ListHeader,
  ListItem,
  ProvidersListWrapper,
  StyledButton,
  StyledProviderContainer,
  StyledProviderIcon,
  StyledTagConnectedDesktop,
  StyledTagConnectedMobile,
} from './ProvidersList.styles';
import { messages } from './messages';
import { PartlyHighlightedText } from './PartlyHighlightedText';

export const isUsingNewRouting = (productType: ProductType) => {
  const allowedProducts = [ProductType.BUSINESS_TRANSACTIONS, ProductType.BUSINESS_ACCOUNT_CHECK];

  return allowedProducts.includes(productType);
};

type BuildRedirectUrlForGroup = {
  providerViewSetGroup: ProviderViewSetGroup;
  version: string;
  productType: ProductType;
  productDimension: ProductDimension;
};

export const buildRedirectUrlForGroup = ({
  providerViewSetGroup,
  version,
  productType,
  productDimension,
}: BuildRedirectUrlForGroup) => {
  const {
    title,
    providers,
    accessTypes,
    credentialsTypes,
    authenticationUserTypes,
    financialInstitutionIds,
  } = providerViewSetGroup;

  if (financialInstitutionIds.length > 1) {
    /**
     * Handle unique `financialInstitutionId` by displaying the financial institution names
     * in the list and upon selecting * them return a new group with only providers from said institution.
     */
    return buildFinancialInstitutionPickerUrl({ version, productType, productDimension, title });
  }

  if (isUsingNewRouting(productType)) {
    const financialInstitutionId = financialInstitutionIds[0];

    return buildFinancialServicePicker({
      providers,
      version,
      productType,
      productDimension,
      financialInstitutionId,
    });
  }

  if (authenticationUserTypes.length > 1) {
    /**
     * Handle unique `authenticationUserType` values (PERSONAL,BUSINESS,CORPORATE).
     */
    const financialInstitutionId = financialInstitutionIds[0];

    return buildAuthenticationTypePickerUrl({
      version,
      productType,
      productDimension,
      financialInstitutionId,
    });
  }

  if (accessTypes.length > 1) {
    /**
     * Handle unique `accessType` values (OPEN_BANKING, OTHER)
     */
    const financialInstitutionId = financialInstitutionIds[0];
    const authenticationUserType = authenticationUserTypes[0];

    return buildMultipleAccessTypePickerUrl({
      version,
      productType,
      productDimension,
      financialInstitutionId,
      authenticationUserType,
    });
  }

  if (credentialsTypes.length > 1) {
    /**
     * Handle unique `credentialsType` values (PASSWORD, MOBILE_BANKID, KEYFOB, THIRD_PARTY_APP)
     * Display the credential type picker (if you want to use BankID, password, etc.)
     */
    const financialInstitutionId = financialInstitutionIds[0];
    const authenticationUserType = authenticationUserTypes[0];
    const accessType = accessTypes[0];

    return buildCredentialsTypeUrl({
      version,
      productType,
      productDimension,
      financialInstitutionId,
      authenticationUserType,
      accessType,
    });
  }

  if (providers.length > 1) {
    if (Provider.hasUniqueDisplayDescriptions(providers)) {
      /**
       * List all remaining providers by `displayDescription`.
       */
      const financialInstitutionId = financialInstitutionIds[0];
      const authenticationUserType = authenticationUserTypes[0];
      const accessType = accessTypes[0];
      const credentialsType = credentialsTypes[0];

      return buildDisplayDescriptionUrl({
        version,
        productType,
        productDimension,
        financialInstitutionId,
        authenticationUserType,
        accessType,
        credentialsType,
      });
    }
  }

  if (providers.length === 1) {
    // We only have a single provider so show credential form
    const providerName = providers[0].name;

    return buildSingleProviderUrl({ version, productType, productDimension, providerName });
  }

  return undefined;
};

type ProviderItemProps = {
  title: string;
  iconUrl?: string;
  onClick: () => void;
  connected?: boolean;
  currentSearchQuery?: string;
};

export const ProviderItem: React.FC<ProviderItemProps> = ({
  title,
  iconUrl,
  onClick,
  connected,
  currentSearchQuery = '',
}) => {
  return (
    <ListItem>
      <StyledButton
        disabled={connected}
        onClick={() => onClick()}
        data-test="providerListItem"
        data-testid={title}
      >
        {connected && (
          <StyledTagConnectedDesktop>
            <FormattedMessage {...messages.connected} />
          </StyledTagConnectedDesktop>
        )}
        {iconUrl && (
          <StyledProviderIcon height="40" src={iconUrl} alt="" role="presentation" loading="lazy" />
        )}
        <StyledProviderContainer>
          <Caption as="span" data-test="providerName">
            <PartlyHighlightedText text={title} textToHighlight={currentSearchQuery} />
          </Caption>
        </StyledProviderContainer>
        {connected && (
          <StyledTagConnectedMobile>
            <FormattedMessage {...messages.connected} />
          </StyledTagConnectedMobile>
        )}
      </StyledButton>
    </ListItem>
  );
};

/**
 * Finds the provider search input by data-test-id attribute
 * and focusses it.
 */
const focusProviderSearchInput = () => {
  (document.querySelector('[data-testid="provider-list-search-field"]') as HTMLElement)?.focus();
};

export type CantFindYourBankItemProps = {
  onClick?: () => void;
};

export const CantFindYourBankItem = ({
  onClick = focusProviderSearchInput,
}: CantFindYourBankItemProps) => {
  return (
    <CantFindYourBankListItem>
      <StyledButton onClick={onClick} data-testid="cantFindYourBankItem">
        <FormattedMessage {...messages.cantFindYourBank} />
      </StyledButton>
    </CantFindYourBankListItem>
  );
};

export type SelectProviderViewSetGroup = (payload: {
  providerViewSetGroup: ProviderViewSetGroup;
}) => void;

export type ProviderListProps = {
  providerViewSetGroups: ProviderViewSetGroup[];
  onProviderItemClick: SelectProviderViewSetGroup;
  currentSearchQuery?: string;
};

export const isProviderConnected = (
  connectedProviders: string | null,
  providers: ProviderEntity[] | undefined
): boolean => {
  if (!connectedProviders || !providers) {
    return false;
  }

  const allNames = providers.map((p) => p.name);

  return allNames.some((nameToCheck) => connectedProviders.includes(nameToCheck));
};

export const ProvidersList: React.FC<ProviderListProps> = ({
  providerViewSetGroups,
  onProviderItemClick,
  currentSearchQuery = '',
}) => {
  const connectedProviders = useConnectedProviders();
  const market = applicationParameters.get('market');

  if (providerViewSetGroups.length === 0) {
    return (
      <EmptyResult>
        <FormattedMessage {...messages.noProviders} />
      </EmptyResult>
    );
  }

  return (
    <ProvidersListWrapper data-testid="providers-list">
      <GradientList>
        <List>
          {providerViewSetGroups.map((setGroup) => {
            return (
              <ProviderItem
                currentSearchQuery={currentSearchQuery}
                connected={isProviderConnected(connectedProviders, setGroup.providers)}
                title={setGroup.title}
                key={setGroup.title}
                iconUrl={setGroup.iconUrl}
                onClick={() => onProviderItemClick({ providerViewSetGroup: setGroup })}
              />
            );
          })}
          {market === MarketCode.US && <CantFindYourBankItem />}
        </List>
      </GradientList>
    </ProvidersListWrapper>
  );
};

export const SectionedProvidersList = ({
  providerViewSetGroups,
  onProviderItemClick,
  currentSearchQuery = '',
}: ProviderListProps) => {
  const market = applicationParameters.get('market');
  const connectedProviders = useConnectedProviders();

  if (providerViewSetGroups.length === 0) {
    return (
      <EmptyResult>
        <FormattedMessage {...messages.noProviders} />
      </EmptyResult>
    );
  }

  const isSearching = currentSearchQuery.length > 0;

  const popularProviders = providerViewSetGroups.filter((p) => p.popular);
  const unpopularProviders = providerViewSetGroups.filter((p) => !p.popular);

  const sectionedProviders = [
    {
      providers: popularProviders,
      label: messages.mostCommon,
      key: 'most-common',
    },
    {
      providers: unpopularProviders,
      label: messages.allOtherBanks,
      key: 'all-banks',
    },
  ].filter(({ providers }) => providers.length !== 0);

  const unsectionedProviders = [
    {
      providers: providerViewSetGroups,
      label: messages.allOtherBanks,
      key: 'all-providers',
    },
  ];

  const providersList = isSearching ? unsectionedProviders : sectionedProviders;

  // In case of only having one group, then we hide the labels.
  const showLabels = providersList.length > 1;

  return (
    <ProvidersListWrapper data-testid="sectioned-provider-list">
      <GradientList>
        {providersList.map(({ providers, label, key }) => (
          <Fragment key={key}>
            {showLabels && (
              <ListHeader mb="xxsmall" mt="xxsmall" as="h2">
                <FormattedMessage {...label} />
              </ListHeader>
            )}
            <List>
              {providers.map((setGroup) => {
                return (
                  <ProviderItem
                    currentSearchQuery={currentSearchQuery}
                    connected={isProviderConnected(connectedProviders, setGroup.providers)}
                    title={setGroup.title}
                    key={setGroup.title}
                    iconUrl={setGroup.iconUrl}
                    onClick={() => onProviderItemClick({ providerViewSetGroup: setGroup })}
                  />
                );
              })}
              {key === 'most-common' && market === MarketCode.US && <CantFindYourBankItem />}
            </List>
          </Fragment>
        ))}
      </GradientList>
    </ProvidersListWrapper>
  );
};
